/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
// Redux Toolkit
import { useSelector } from "react-redux";
import { selectUser } from "../../store/userSlice";
// actions
import { singleResource, deleteInstance } from "../../actions"
// BASE Components
import BASESvgIcon from "../../../@BASE/core/BASESvgIcon";
import NavLinkAdapter from "../../../@BASE/core/NavLinkAdapter";
import authRoles from "../../auth/authRoles";
// Material-UI Components
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
// custom components
import ViewSubheader from "../crud/subheaders/ViewSubheader";
import EditSubheader from "../crud/subheaders/EditSubheader";
import DeleteModal from "../crud/DeleteModal";

function InstanceHeader(props) {
  // destructure props
  const { instance, details, resource, action, onSave, save_disabled, handleInstanceAction } = props;
  // get the route parameters and navigate function
  const routeParams = useParams();
  // useNavigate hook for navigation
  const navigate = useNavigate();
  // get the current user from the Redux store
  const user = useSelector(selectUser);
  // function to handle the subheader object
  const handleSubheaderObject = (path, icon, label, value) => {
    const data = {
      path: path,
      icon: icon,
      label: label,
      value: value,
    };

    return data;
  }
  // function to handle the removal of a resource
  const handleRemoveResource = () => {
    const data = {
      id: instance.id,
      data: {
        user: user._id || user.id
      }
    };
    handleInstanceAction(deleteInstance(resource)(data), 'delete');

    if (resource === 'projects' || resource === 'organizations' || resource === 'users' || resource === 'templates') {
      navigate(`/${resource}`);
    } else {
      navigate(`/${details.parent.resource}/${details.parent.id}`);
    }
  }
  // function to handle the icon for editing an instance based on the resource type
  const handleEditInstanceIcon = (resource) => {
    switch (resource) {
      case "courses":
        return "heroicons-outline:view-grid";
      case "lessons":
        return "heroicons-outline:cube";
      case "sections":
        return "heroicons-outline:folder";
      case "pages":
        return "heroicons-outline:document";
      case "templates":
        return "heroicons-outline:template";
      case "users":
        return "heroicons-outline:user";
      case "organizations":
        return "heroicons-outline:office-building";
      default:
        return "heroicons-outline:cube";
    }
  }
  // function to handle the subheader based on the action
  const handleSubheader = (action) => {
    switch (action) {
      case "edit":
        let editData;
        switch (routeParams.id) {
          case "new":
            editData = handleSubheaderObject(null, "heroicons-outline:plus", handleEditInstanceIcon(resource), `Use the form below to create a new ${singleResource(resource)} instance.`);
            break;
          default:
            editData = handleSubheaderObject(null, "heroicons-outline:pencil-alt", handleEditInstanceIcon(resource), `Use the form below to edit the ${singleResource(resource)} details.`);
        }
        return (
          <EditSubheader data={editData} />
        );
      default:
        const data = [];
        switch (resource) {
          case "courses":
            data[0] = handleSubheaderObject(`${window.location.origin}/projects`, "heroicons-outline:collection", "Project", instance?.project?.label);
            data[1] = handleSubheaderObject(null, "heroicons-outline:view-grid", "Course", instance.label);
            break;
          case "lessons":
            data[0] = handleSubheaderObject(`${window.location.origin}/projects`, "heroicons-outline:collection", "Project", instance?.course?.project?.label);
            data[1] = handleSubheaderObject(`../courses/${instance?.course?.id}`, "heroicons-outline:view-grid", "Course", instance?.course?.label);
            data[2] = handleSubheaderObject(null, "heroicons-outline:cube", "Lesson", instance.label);
            break;
          case "sections":
            data[0] = handleSubheaderObject(`${window.location.origin}/projects`, "heroicons-outline:collection", "Project", instance?.lesson?.course?.project?.label);
            data[1] = handleSubheaderObject(`../courses/${instance?.lesson?.course?.id}`, "heroicons-outline:view-grid", "Course", instance?.lesson?.course?.label);
            data[2] = handleSubheaderObject(`../lessons/${instance?.lesson?.id}`, "heroicons-outline:cube", "Lesson", instance?.lesson?.label);
            data[3] = handleSubheaderObject(null, "heroicons-outline:folder", "Section", instance.label);
            break;
          case "templates":
            data[0] = handleSubheaderObject(null, "heroicons-outline:color-swatch", "Classification", instance.classification);
            data[1] = handleSubheaderObject(null, "heroicons-outline:tag", "Label", instance.label);
            break;
          default:
            data[0] = handleSubheaderObject(`${window.location.origin}/projects`, "heroicons-outline:collection", "Project", instance?.section?.lesson?.course?.project?.label);
            data[1] = handleSubheaderObject(`../courses/${instance?.section?.lesson?.course?.id}`, "heroicons-outline:view-grid", "Course", instance?.section?.lesson?.course?.label);
            data[2] = handleSubheaderObject(`../lessons/${instance?.section?.lesson?.id}`, "heroicons-outline:cube", "Lesson", instance?.section?.lesson?.label);
            data[3] = handleSubheaderObject(`../sections/${instance.section?.id}`, "heroicons-outline:folder", "Section", instance?.section?.label);
            data[4] = handleSubheaderObject(null, "heroicons-outline:document-text", "Page ID", instance.label);
        }

        return (
          <ViewSubheader data={data} action={action} />
        );
    }
  }

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleCommentClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCommentClose = () => {
    setAnchorEl(null);
  };

  // if loading, show loading spinner
  if (!details || !instance) {
    return <CircularProgress />;
  }

  console.log(action)
  
  return (
    <div className="p-24 sm:p-32 sm:pb-64 w-full">
      {resource === 'pages' && action !== 'edit' ? null : (
        <div className="flex items-center">
          <Typography
            className="flex items-center"
            component={Link}
            role="button"
            to={`/${details.return.path}`}
            color="inherit"
          >
            <BASESvgIcon size={20}>heroicons-outline:arrow-sm-left</BASESvgIcon>
            <span className="flex mx-4 font-medium capitalize">{details.return.label}</span>
          </Typography>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center mt-24">
        <div className="grow">
          <div className="flex items-center mb-24">
            {(action === "comments" || action === "imi-comments") && (
              <BASESvgIcon size={32} className="mr-8">heroicons-outline:chat-alt-2</BASESvgIcon>
            )}
            {action === "pre-trial-comments" && (
              <BASESvgIcon size={32} className="mr-8">heroicons-outline:clipboard-check</BASESvgIcon>
            )}
            <Typography className="text-2xl md:text-3xl font-extrabold tracking-tight leading-none mb-0">{details.title}</Typography>
          </div>
          {handleSubheader(action)}
        </div>
        <div className="flex-none">
          {action === "edit" && (
            <>
              {routeParams.id !== "new" && (
                <DeleteModal onDelete={handleRemoveResource} resource={resource} />
              )}
              <Button
                variant="contained"
                disabled={save_disabled}
                onClick={onSave}
                sx={{
                  backgroundColor: 'common.white',
                  color: 'primary.dark',
                }}
              >
                Save
              </Button>
            </>
          )}
          {action === "view" && (
            <>
              {authRoles.leadEditor.includes(user.role) && (
                <Tooltip title={`Edit ${singleResource(resource)}`} arrow>
                  <Button
                    variant="contained"
                    component={NavLinkAdapter}
                    to={`edit${details?.parent?.id != null ? `?p=${details?.parent?.id}` : ''}`}
                    sx={{
                      backgroundColor: 'common.white',
                      color: 'primary.dark',
                    }}
                  >
                    <BASESvgIcon size={20}>
                      heroicons-outline:pencil-alt
                    </BASESvgIcon>
                    <span className="mx-8">Edit</span>
                  </Button>
                </Tooltip>
              )}
              {!["projects", "organizations", "templates"].includes(resource) && (
                <Tooltip title="View Comments" arrow>
                  <>
                    <Button
                      variant="contained"
                      id="view-comments-button"
                      aria-controls="view-comments-menu"
                      aria-haspopup="true"
                      aria-expanded={open ? 'true' : undefined}
                      onClick={handleCommentClick}
                      sx={{
                        backgroundColor: 'common.white',
                        color: 'primary.dark',
                      }}
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:chat-alt-2</BASESvgIcon>}
                      endIcon={<BASESvgIcon size={10}>heroicons-outline:chevron-down</BASESvgIcon>}
                      className="ml-8"
                    >
                      Comments
                    </Button>
                    <Menu
                      id="view-comments-menu"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleCommentClose}
                      MenuListProps={{
                        'aria-labelledby': 'view-comments-button',
                      }}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      dense='true'
                    >

                      <MenuItem
                        dense
                        component={NavLinkAdapter}
                        to="comments"
                        onClick={handleCommentClose}
                      >
                        <ListItemIcon>
                          <BASESvgIcon size={20}>
                            heroicons-outline:document-text
                          </BASESvgIcon>
                        </ListItemIcon>
                        <ListItemText>Storyboard Comments</ListItemText>
                      </MenuItem>
                      <MenuItem
                        dense
                        component={NavLinkAdapter}
                        to="imi-comments"
                        onClick={handleCommentClose}
                      >
                        <ListItemIcon>
                          <BASESvgIcon size={20}>
                            heroicons-outline:desktop-computer
                          </BASESvgIcon>
                        </ListItemIcon>
                        <ListItemText>Production Comments</ListItemText>
                      </MenuItem>
                      <MenuItem
                        dense
                        component={NavLinkAdapter}
                        to="pre-trial-comments"
                        onClick={handleCommentClose}
                      >
                        <ListItemIcon>
                          <BASESvgIcon size={20}>
                            heroicons-outline:clipboard-check
                          </BASESvgIcon>
                        </ListItemIcon>
                        <ListItemText>Pre-Trial Comments</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default InstanceHeader;
